import React, { useState, useEffect } from 'react';
import { FovModal, FovSelect, FovOption, FovToggle, VerticalSpacer } from 'components';
import {
  ModelSelectWrapper,
  SimpleFieldLabel,
  SimpleFieldInput,
  SimpleToggleRow,
  GenerateButton,
} from '../imageCanvasStyles';
import {
  TENCENT_MODELS,
  TENCENT_GENERATE_TYPES,
  TENCENT_POLYGON_TYPES,
  TENCENT_FACE_COUNT_MIN,
  TENCENT_FACE_COUNT_MAX,
  validateTencentSettings,
} from '../tencentSettings';

const GENERATE_TYPES_BY_MODEL = model => TENCENT_GENERATE_TYPES.filter(
  generateType => model !== '3.1' || (generateType !== 'LowPoly' && generateType !== 'Sketch'),
);

const POLYGON_TYPE_LABELS = { triangle: 'Triangle', quadrilateral: 'Quadrilateral' };

const TencentSettingsModal = ({ theme, selectedTheme, visible, settings, onCancel, onSave }) => {
  const shared = { theme, $selectedTheme: selectedTheme };
  const [ draft, setDraft ] = useState(settings);

  useEffect(() => {
    if (visible) setDraft(settings);
  }, [ visible, settings ]);

  const handleModelChange = value => {
    const availableGenerateTypes = GENERATE_TYPES_BY_MODEL(value);
    const generateType = availableGenerateTypes.includes(draft.generateType) ? draft.generateType : 'Normal';
    setDraft({ ...draft, model: value, generateType });
  };

  const handleFaceCountChange = e => {
    setDraft({ ...draft, faceCount: e.target.value });
  };

  const handleFaceCountBlur = () => {
    const clamped = Math.min(
      TENCENT_FACE_COUNT_MAX,
      Math.max(TENCENT_FACE_COUNT_MIN, Number(draft.faceCount) || TENCENT_FACE_COUNT_MIN),
    );
    setDraft({ ...draft, faceCount: clamped });
  };

  const handleSave = () => {
    const { valid } = validateTencentSettings(draft);
    if (!valid) return;
    onSave(draft);
  };

  const generateTypeOptions = GENERATE_TYPES_BY_MODEL(draft.model);
  const isGeometry = draft.generateType === 'Geometry';
  const isLowPoly = draft.generateType === 'LowPoly';

  return (
    <FovModal title='Tencent AI Settings' visible={visible} onClose={onCancel} width={360}>
      <SimpleFieldLabel {...shared}>Model</SimpleFieldLabel>
      <ModelSelectWrapper>
        <FovSelect
          value={draft.model}
          placeholder={draft.model}
          onChange={(e, newVal) => handleModelChange(newVal)}
        >
          {TENCENT_MODELS.map(value => (
            <FovOption key={value} value={value}>{value}</FovOption>
          ))}
        </FovSelect>
      </ModelSelectWrapper>

      <SimpleFieldLabel {...shared}>Generate Type</SimpleFieldLabel>
      <ModelSelectWrapper>
        <FovSelect
          value={draft.generateType}
          placeholder={draft.generateType}
          onChange={(e, newVal) => setDraft({ ...draft, generateType: newVal })}
        >
          {generateTypeOptions.map(value => (
            <FovOption key={value} value={value}>{value}</FovOption>
          ))}
        </FovSelect>
      </ModelSelectWrapper>

      {isLowPoly && (
        <>
          <SimpleFieldLabel {...shared}>Polygon Type</SimpleFieldLabel>
          <ModelSelectWrapper>
            <FovSelect
              value={draft.polygonType}
              placeholder={POLYGON_TYPE_LABELS[draft.polygonType]}
              onChange={(e, newVal) => setDraft({ ...draft, polygonType: newVal })}
            >
              {TENCENT_POLYGON_TYPES.map(value => (
                <FovOption key={value} value={value}>{POLYGON_TYPE_LABELS[value]}</FovOption>
              ))}
            </FovSelect>
          </ModelSelectWrapper>
        </>
      )}

      <SimpleFieldLabel {...shared}>Face Count</SimpleFieldLabel>
      <SimpleFieldInput
        {...shared}
        type='number'
        min={TENCENT_FACE_COUNT_MIN}
        max={TENCENT_FACE_COUNT_MAX}
        value={draft.faceCount}
        onChange={handleFaceCountChange}
        onBlur={handleFaceCountBlur}
      />
      <VerticalSpacer size={15} />
      <SimpleToggleRow {...shared}>
        Enable PBR

        <FovToggle
          name='enablePBRToggle'
          checked={!!draft.enablePBR}
          disabled={isGeometry}
          callback={value => setDraft({ ...draft, enablePBR: value })}
        />
      </SimpleToggleRow>

      <VerticalSpacer size={20} />

      <GenerateButton {...shared} onClick={handleSave}>
        Save
      </GenerateButton>
    </FovModal>
  );
};

export { TencentSettingsModal };
